import React from 'react';
import type { Control } from 'react-hook-form';
import { DevTool } from '@hookform/devtools';

import { Flex } from '../../core';

import FormAlert, { Props as FormAlertProps } from './FormAlert';

export interface Props extends FormAlertProps {
  /** Form fields and content */
  children: React.ReactNode;
  /** Form submission handler */
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  /** React Hook Form control for dev tools */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- allow for flexibility in `control` type
  control?: Control<any>;
}

/**
 * Form wrapper component that provides form structure and alerts.
 * Integrates with react-hook-form dev tools when control is provided.
 *
 * Usage guide: `kibble/forms/Overview.md`.
 */
const Form = ({
  children,
  errorMessage,
  onSubmit,
  successMessage,
  control,
}: Props): JSX.Element => {
  return (
    <Flex flexDirection="column">
      <FormAlert errorMessage={errorMessage} successMessage={successMessage} />
      {/*
        method="post" so a submit that escapes React (before hydration, or if
        hydration fails) is sent natively as a POST. A native GET would put
        every named field in the URL query string, where access logs capture
        them.
      */}
      <Flex webAs="form" webAsProps={{ onSubmit, method: 'post' }} flexDirection="column" flex="1">
        {children}
      </Flex>
      {control ? <DevTool control={control} /> : null}
    </Flex>
  );
};

export default Form;
